<template>
  <b-modal
    v-model="open"
    title="Anexar comprovante da saida"
    ok-title="Anexar"
    cancel-title="Cancelar"
    :ok-disabled="!receiptPhoto"
    @ok.prevent="onOk"
    @cancel="onCancel"
    @close="onCancel"
  >
    <b-alert v-if="error" show variant="danger" class="py-2">
      <span class="fas fa-exclamation-triangle mr-1" />
      {{ error }}
    </b-alert>

    <b-form-group
      label="Foto da nota ou comprovante"
      label-for="deposit-receipt-photo"
    >
      <b-form-file
        id="deposit-receipt-photo"
        accept="image/*"
        browse-text="Escolher"
        placeholder="Selecione a imagem do comprovante"
        @input="onFileInput"
      />
    </b-form-group>

    <div class="receipt-actions mt-2">
      <b-button size="sm" variant="outline-primary" @click="startCamera">
        <span class="fas fa-camera mr-1" />
        {{ cameraActive ? 'Reiniciar camera' : 'Tirar foto' }}
      </b-button>

      <b-button
        v-if="cameraActive"
        size="sm"
        variant="primary"
        @click="capturePhoto"
      >
        <span class="fas fa-dot-circle mr-1" />
        Capturar
      </b-button>
    </div>

    <div v-if="cameraActive" class="receipt-camera mt-2">
      <video
        ref="receiptVideo"
        class="receipt-camera-video"
        autoplay
        muted
        playsinline
      />
    </div>

    <div v-else-if="previewUrl" class="receipt-preview mt-2">
      <img :src="previewUrl" alt="Nota ou comprovante da saida" />
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'DepositReceiptModal',
  props: {
    value: {
      type: Boolean
    }
  },
  data() {
    return {
      receiptPhoto: null,
      previewUrl: null,
      error: null,
      cameraActive: false,
      cameraStream: null
    }
  },
  computed: {
    open: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return this.value
      }
    }
  },
  beforeDestroy() {
    this.stopCamera()
    this.clearPreview()
  },
  methods: {
    onOk() {
      if (!this.receiptPhoto) {
        this.error = 'Selecione ou tire uma foto do comprovante.'
        return
      }

      this.$emit('ok', { receiptPhoto: this.receiptPhoto })
    },
    onCancel() {
      this.$emit('cancel')
    },
    onFileInput(file) {
      if (file) {
        this.setReceiptPhoto(file)
      }
    },
    async startCamera() {
      this.error = null
      this.stopCamera()

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.error = 'Este dispositivo ou navegador nao permite abrir a camera.'
        return
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' }
          },
          audio: false
        })
        this.cameraStream = stream
        this.cameraActive = true

        await this.$nextTick()
        if (this.$refs.receiptVideo) {
          this.$refs.receiptVideo.srcObject = stream
          await this.$refs.receiptVideo.play()
        }
      } catch (error) {
        this.cameraActive = false
        this.error = this.getCameraErrorMessage(error)
      }
    },
    capturePhoto() {
      const video = this.$refs.receiptVideo

      if (!video || !video.videoWidth || !video.videoHeight) {
        this.error =
          'Nao foi possivel capturar a foto. Aguarde a camera carregar e tente novamente.'
        return
      }

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const context = canvas.getContext('2d')
      context.drawImage(video, 0, 0, canvas.width, canvas.height)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            this.error = 'Nao foi possivel gerar a imagem do comprovante.'
            return
          }

          const file = new File([blob], `comprovante-saida-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          })
          this.setReceiptPhoto(file)
          this.stopCamera()
        },
        'image/jpeg',
        0.9
      )
    },
    setReceiptPhoto(file) {
      this.error = null

      if (!file.type || !file.type.startsWith('image/')) {
        this.error = 'Selecione uma imagem valida do comprovante.'
        return
      }

      if (file.size > 8 * 1024 * 1024) {
        this.error = 'A foto do comprovante deve ter no maximo 8 MB.'
        return
      }

      this.clearPreview()
      this.receiptPhoto = file
      this.previewUrl = URL.createObjectURL(file)
    },
    stopCamera() {
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach((track) => track.stop())
      }
      this.cameraStream = null
      this.cameraActive = false
    },
    clearPreview() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.previewUrl = null
    },
    getCameraErrorMessage(error) {
      if (error && error.name === 'NotAllowedError') {
        return 'Permita o acesso a camera para fotografar o comprovante.'
      }

      if (error && error.name === 'NotFoundError') {
        return 'Nao encontramos camera disponivel neste dispositivo.'
      }

      if (error && error.name === 'NotReadableError') {
        return 'A camera esta em uso por outro aplicativo. Feche-o e tente novamente.'
      }

      return 'Nao foi possivel abrir a camera. Verifique as permissoes e tente novamente.'
    }
  }
}
</script>

<style scoped>
.receipt-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.receipt-camera,
.receipt-preview {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  overflow: hidden;
}

.receipt-camera-video,
.receipt-preview img {
  display: block;
  max-height: 220px;
  object-fit: contain;
  width: 100%;
}
</style>
